import React from 'react';
import { Box, CardContent, Typography, Grid } from '@material-ui/core';
import { formatBillingFrequency } from 'utils/MembershipUtils';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxTaped, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import moment, { Moment } from 'moment-timezone';
import { Enrollment } from '@spike/membership-model';
import { getEnrollmentStartDate, getEnrollmentEndDate } from 'utils/EnrollmentUtils';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { ConfirmDialog } from 'components/UI';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper, usePermissions, useTimeZone } from 'hooks';
import { MembershipsStatus, updateEnrollmentEndAtOrPausedAtThunk } from '@spike/memberships-action';
import { RootState } from 'store';
import styles from './Membership.module.scss';

interface MembershipProps {
    enrollment: Enrollment;
    classes: { [key: string]: string };
    cardIcon: string;
    onSaved?: () => void;
}

const Membership: React.FC<MembershipProps> = ({ enrollment, classes, cardIcon, onSaved }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const permissions = usePermissions();
    const canEditEndDate = permissions.memberships.editEndDate;
    const [isEndDateSelectorExpanded, setIsEndDateSelectorExpanded] = React.useState(false);
    const [draftEndDate, setDraftEndDate] = React.useState<Moment | null>(null);
    const [endDateOverride, setEndDateOverride] = React.useState<Moment | null | undefined>(undefined);
    const [endDateValidationError, setEndDateValidationError] = React.useState<string | null>(null);
    const [savingEndDate, setSavingEndDate] = React.useState(false);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = React.useState(false);
    const pendingEndDateRef = React.useRef<Moment | null | undefined>(undefined);

    const closeEndDateEditor = React.useCallback(() => {
        setIsEndDateSelectorExpanded(false);
        setDraftEndDate(null);
        setEndDateValidationError(null);
    }, []);

    React.useEffect(() => {
        setEndDateOverride(undefined);
        closeEndDateEditor();
    }, [closeEndDateEditor, enrollment.id]);

    React.useEffect(() => {
        if (!savingEndDate) {
            return;
        }

        if (membershipsStatus === MembershipsStatus.UpdateEndDateSuccess) {
            const pendingEndDate = pendingEndDateRef.current ?? null;
            setEndDateOverride(pendingEndDate);
            closeEndDateEditor();
            onSaved?.();
            pendingEndDateRef.current = undefined;
            setSavingEndDate(false);
            setShowRemoveConfirmation(false);
        }

        if (membershipsStatus === MembershipsStatus.UpdateEndDateError) {
            pendingEndDateRef.current = undefined;
            setSavingEndDate(false);
            setShowRemoveConfirmation(false);
        }
    }, [closeEndDateEditor, membershipsStatus, onSaved, savingEndDate]);

    const getPaymentMethod = () => {
        if (enrollment.paymentMethod) {
            return enrollment.paymentMethod.lastDigits;
        }
        if (enrollment.paid === true && enrollment.paymentMethod === null) {
            return 'Cash/Other';
        }
        return '';
    };

    const getEffectiveEndDate = (): Moment | string | null => {
        if (endDateOverride !== undefined) {
            return endDateOverride;
        }

        return getEnrollmentEndDate(enrollment) || null;
    };

    const getValidEndDateMoment = (): Moment | null => {
        const endDate = getEffectiveEndDate();

        return endDate && moment(endDate).isValid() ? moment(endDate).tz(timezone) : null;
    };

    const formatEndDate = (): string => {
        const endDate = getEffectiveEndDate();

        if (endDate == null || endDate === '') {
            return '';
        }

        return moment(endDate).isValid() ? moment(endDate).tz(timezone).format('MMM DD, YYYY') : String(endDate);
    };

    const getEmptyEndDateLabel = (): string => {
        return enrollment.membership.enrollmentType === 'subscription' ? 'Until Canceled' : 'Never Expires';
    };

    const isEndDateEditable = (): boolean => {
        return (
            canEditEndDate &&
            ['package', 'subscription'].includes(enrollment.membership.enrollmentType) &&
            !['canceled', 'cancelled', 'ended'].includes(enrollment.status)
        );
    };

    const canRemoveEndDate = (): boolean => {
        return (
            enrollment.membership.enrollmentType === 'subscription' ||
            enrollment.membership.packageExpiration === 'never' ||
            !enrollment.membership.packageExpiration ||
            enrollment.membership.packageExpiration === 'limited'
        );
    };

    const requiresRemoveConfirmation = (): boolean => {
        return (
            enrollment.membership.enrollmentType === 'package' && enrollment.membership.packageExpiration === 'limited'
        );
    };

    const getValidationError = (endDate: Moment | null): string | null => {
        if (!endDate) {
            return null;
        }

        const startDate = moment(getEnrollmentStartDate(enrollment)).tz(timezone).startOf('day');
        const selectedEndDate = endDate.clone().tz(timezone).startOf('day');
        const today = moment().tz(timezone).startOf('day');

        if (selectedEndDate.isBefore(startDate, 'day')) {
            return 'End Date cannot be before Start Date.';
        }

        if (selectedEndDate.isBefore(today, 'day')) {
            return 'End Date cannot be in the past.';
        }

        return null;
    };

    const openEndDateEditor = () => {
        if (!isEndDateEditable() || enrollment.status === 'paused') {
            return;
        }

        setDraftEndDate(getValidEndDateMoment());
        setIsEndDateSelectorExpanded(!isEndDateSelectorExpanded);
        setEndDateValidationError(null);
    };

    const saveEndDate = async (endDate: Moment | null) => {
        const validationError = getValidationError(endDate);

        if (validationError) {
            setEndDateValidationError(validationError);
            return;
        }

        setSavingEndDate(true);
        pendingEndDateRef.current = endDate;

        const formattedEndDate = endDate ? endDate.clone().endOf('day').toISOString() : null;
        const formattedPausedAt = enrollment.pausedAt ? enrollment.pausedAt.toISOString() : null;

        dispatch(
            updateEnrollmentEndAtOrPausedAtThunk(apiClientWrapper, enrollment.id, formattedEndDate, formattedPausedAt)
        );
    };

    const removeEndDate = () => {
        if (!canRemoveEndDate()) {
            return;
        }

        if (requiresRemoveConfirmation()) {
            setShowRemoveConfirmation(true);
            return;
        }

        saveEndDate(null);
    };

    const renderEndDate = () => {
        const formattedEndDate = formatEndDate();
        const editable = isEndDateEditable();
        const isPaused = enrollment.status === 'paused';
        const endDateLabel = formattedEndDate || getEmptyEndDateLabel();

        if (!editable) {
            return <Typography variant="subtitle1">{formattedEndDate || getEmptyEndDateLabel()}</Typography>;
        }

        return (
            <Box>
                <ExpandableSelector
                    title=""
                    id={`enrollment-end-date-${enrollment.id}`}
                    expanded={isEndDateSelectorExpanded}
                    childrenClassName={styles.dateSelector}
                    label={endDateLabel}
                    disabled={isPaused}
                    onExpand={openEndDateEditor}
                    onClickAway={closeEndDateEditor}
                    dropdownAlign="right"
                >
                    <DatePicker
                        minDate={moment.max(
                            moment().tz(timezone).startOf('day'),
                            moment(getEnrollmentStartDate(enrollment)).tz(timezone).startOf('day')
                        )}
                        timeZone={timezone}
                        value={draftEndDate}
                        onChange={date => {
                            setDraftEndDate(date);
                            setEndDateValidationError(getValidationError(date));
                        }}
                    />
                    {endDateValidationError && (
                        <Typography className={styles.endDateError}>{endDateValidationError}</Typography>
                    )}
                    <Box className={styles.endDateActions}>
                        {canRemoveEndDate() && (
                            <button
                                type="button"
                                className={styles.endDateActionButton}
                                disabled={savingEndDate}
                                onClick={removeEndDate}
                            >
                                {getEmptyEndDateLabel()}
                            </button>
                        )}
                        <button
                            type="button"
                            className={clsx(styles.endDateActionButton, styles.endDateSaveButton)}
                            disabled={savingEndDate || Boolean(endDateValidationError)}
                            onClick={() => saveEndDate(draftEndDate)}
                        >
                            Save
                        </button>
                    </Box>
                </ExpandableSelector>
                {isPaused && (
                    <Typography className={styles.pausedMessage}>
                        End Date can only be edited after the enrollment is Resumed
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <>
            <Box
                className={clsx(classes.cardStyle)}
                style={{ marginBottom: 28 }}
            >
                <CardContent className={clsx(classes.cardContent, classes.cardWide)}>
                    <Typography
                        variant="h6"
                        className={classes.cardTitle}
                    >
                        Membership Details
                    </Typography>
                    <Grid
                        container
                        spacing={2}
                        style={{ marginTop: 10 }}
                    >
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            lg={3}
                        >
                            <Typography
                                variant="body2"
                                className={classes.titleLabel}
                            >
                                Type
                            </Typography>
                            <Typography variant="subtitle1">
                                <FontAwesomeIcon
                                    className={classes.subscriptionTypeItemIcon}
                                    icon={enrollment.membership.enrollmentType === 'package' ? faBoxTaped : faRefresh}
                                />
                                {enrollment.membership.enrollmentType === 'package' ? 'Package' : 'Subscription'}
                            </Typography>
                        </Grid>

                        {enrollment.membership.enrollmentType != 'package' && (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                lg={3}
                            >
                                <Typography
                                    variant="body2"
                                    className={classes.titleLabel}
                                >
                                    Frequency
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    className={classes.labelValue}
                                >
                                    {formatBillingFrequency(enrollment.membership)}
                                </Typography>
                            </Grid>
                        )}
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            lg={3}
                        >
                            <Typography
                                variant="body2"
                                className={classes.titleLabel}
                            >
                                Start Date
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                className={classes.labelValue}
                            >
                                {moment(getEnrollmentStartDate(enrollment)).format('MMM DD, YYYY')}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            lg={3}
                        >
                            <Typography
                                variant="body2"
                                className={classes.titleLabel}
                            >
                                End Date
                            </Typography>
                            {renderEndDate()}
                        </Grid>
                        {enrollment.nextBillingDate && enrollment.membership.enrollmentType != 'package' && (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                lg={3}
                            >
                                <Typography
                                    variant="body2"
                                    className={classes.titleLabel}
                                >
                                    Next Billing
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    className={classes.labelValue}
                                >
                                    {moment(enrollment.nextBillingDate).format('MMM DD, YYYY')}
                                </Typography>
                            </Grid>
                        )}
                        {getPaymentMethod() && (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                lg={3}
                            >
                                <Typography
                                    variant="body2"
                                    className={classes.titleLabel}
                                >
                                    Payment Method
                                </Typography>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                >
                                    {enrollment.paymentMethod && (
                                        <img
                                            src={cardIcon}
                                            alt="Visa"
                                            style={{ width: 32, marginRight: 8 }}
                                        />
                                    )}
                                    <Typography
                                        variant="subtitle1"
                                        className={classes.labelValue}
                                    >
                                        {enrollment.paymentMethod?.lastDigits || getPaymentMethod()}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            lg={3}
                        >
                            <Typography
                                variant="body2"
                                className={classes.titleLabel}
                            >
                                Total
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                className={classes.labelValue}
                            >
                                {/* Subscriptions show the recurring price (what future cycles charge);
                                    a first-cycle override only affects the first cycle, not this value. */}
                                $
                                {Number(
                                    enrollment.membership.enrollmentType === 'subscription'
                                        ? enrollment.membership.total
                                        : enrollment.totalAmount
                                ).toFixed(2)}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Box>

            <ConfirmDialog
                open={showRemoveConfirmation}
                title="Remove End Date?"
                question="This package was configured with an expiration. Are you sure you want to remove the End Date?"
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Remove"
                processing={savingEndDate}
                onCancel={() => setShowRemoveConfirmation(false)}
                onConfirm={() => saveEndDate(null)}
            />
        </>
    );
};

export default Membership;
